import { useAppForm } from "../hooks/surveyResponseFormHooks";
import { useSurveyLayout } from "../useSurveyLayout";
import { useMutation } from "@tanstack/react-query";
import { submitSurvey, type Answer } from "@pulse/api/surveyApi";
import { useEffect, useRef } from "react";
import { questionValidator } from "../validators/questions-validator";
import {
    NextButton,
    PreviousButton,
    SkipQuestion,
    SkipQuestionAndSubmitButton,
    SubmitButton,
} from "../questionComponents/FormButtons";
import StatusBar from "./StatusBar";
import { SurveyQuestion } from "../questionComponents/SurveyQuestion";

export default function SurveyForm() {
    const { currentQuestionIndex, questions, setStepType, survey, isPreview } = useSurveyLayout();
    const questionContentRef = useRef<HTMLDivElement>(null);
    const validators = questionValidator(questions || []);
    const total = questions?.length || 0;
    const isLastQuestion = currentQuestionIndex === total - 1;
    const submitMutation = useMutation({
        mutationFn: submitSurvey,
        onSuccess: () => {
            setStepType("complete");
        },
        onError: (error) => {
            console.error("Error submitting survey:", error);
        },
    });

    const form = useAppForm({
        defaultValues: {
            answers: questions?.map((q) => {
                return {
                    surveyQuestionId: q.id,
                };
            }) as Answer[],
        },
        validators: {
            onChange: validators,
        },
        onSubmit: async ({ value }) => {
            if (!survey || !questions) return;

            if (isPreview) {
                setStepType("complete");
                return;
            }

            const filteredAnswers = value.answers.filter((a) => a.answerValue !== null);

            await submitMutation.mutateAsync({
                surveyId: survey.id,
                data: filteredAnswers,
            });
        },
    });

    // Clear error when user changes questions
    useEffect(() => {
        const answerFieldName = `answers[${currentQuestionIndex}].answerValue` as const;
        const commentFieldName = `answers[${currentQuestionIndex}].answerComment` as const;

        questionContentRef.current?.scrollTo({
            top: 0,
        });

        form.setFieldMeta(answerFieldName, (prev) => ({
            ...prev,
            errorMap: { onChange: undefined, onSubmit: undefined },
        }));

        form.setFieldMeta(commentFieldName, (prev) => ({
            ...prev,
            errorMap: { onChange: undefined, onSubmit: undefined },
        }));
    }, [currentQuestionIndex, form]);

    const currentQuestion = questions?.[currentQuestionIndex];

    return (
        <form
            className="flex min-h-0 flex-1 flex-col"
            onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                form.handleSubmit();
            }}
        >
            <StatusBar form={form} />
            <form.AppField name="answers" mode="array">
                {(field) =>
                    field.state.value?.[currentQuestionIndex] && (
                        <SurveyQuestion
                            form={form}
                            key={currentQuestionIndex}
                            questionId={field.state.value?.[currentQuestionIndex]?.surveyQuestionId}
                        />
                    )
                }
            </form.AppField>
            <div className="flex items-start justify-between mb-6">
                <form.AppForm>
                    <PreviousButton form={form} />
                </form.AppForm>
                <div className="flex flex-col items-end gap-4">
                    {!isLastQuestion ? (
                        <form.AppForm>
                            <NextButton form={form} />
                        </form.AppForm>
                    ) : (
                        <form.AppForm>
                            <SubmitButton form={form} />
                        </form.AppForm>
                    )}
                    {currentQuestion?.optional && !isLastQuestion && (
                        <form.AppForm>
                            <SkipQuestion form={form} />
                        </form.AppForm>
                    )}
                    {currentQuestion?.optional && isLastQuestion && (
                        <form.AppForm>
                            <SkipQuestionAndSubmitButton form={form} />
                        </form.AppForm>
                    )}
                </div>
            </div>
        </form>
    );
}
